import { MDropzone, MSpinner } from "components/Shared";
import { EmployeeComplaintResponse } from "interfaces/Mueller.Business.Models.ts";
import WebConfig from "middlewares/WebConfig";
import React, { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";

interface Props {
  canAddAttachments: boolean,
  onAttachmentAdd?: (attachment: any, filename: string) => void,
  onAttachmentBeginLoad?: () => void,
  onAttachmentDelete?: (files: File[]) => void,
  complaint?: EmployeeComplaintResponse,
  canDownloadFiles: boolean,
  canDeleteFiles: boolean,
}

interface FileAttachment {
  canDelete: boolean,
  downloadUrl: string,
  fileName: string,
}


const EmployeeComplaintFileManager = (props: Props) => {

  const [complaintAttachments, setComplaintAttachments] = useState<FileAttachment[]>();
  const [isSettingAttachments, setIsSettingAttachments] = useState<boolean>(true);

  const mounted = useRef(true);

  const dropzoneRightsOverrides = {
    download: props.canDownloadFiles,
    preview: false,
    delete: props.canDeleteFiles,
  };

  const { webApiRoot } = WebConfig;

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    // This component is rendered for the view of a complaint, along with when adding a new one. 
    // If there is a complaint, check for attachments and then render after mapping them. Otherwise, just render this component
    // If there is no complaint, simply render this component
    if (props.complaint) {
      if (Object.keys(props.complaint).includes("attachments") && props.complaint.attachments.length > 0) {
        setComplaintAttachments(props.complaint.attachments.map(a => {
          return ({
            "canDelete": false,
            "downloadUrl": `${webApiRoot}/employee-complaint/attachment/${a.lookupKey}`,
            "fileName": `${a.fileName}`,
          });
        }));
      }
      else {
        setIsSettingAttachments(false);
      }
    }
    else
      setIsSettingAttachments(false);
  }, [props.complaint]);

  // If there is a complaint, and it has attachments, when attachment state is updated, check to make sure they're all loaded. Only render once all files are loaded
  useEffect(() => {
    if (props.complaint) {
      if (Object.keys(props.complaint).includes("attachments") && props.complaint.attachments.length > 0 && complaintAttachments)
        if (complaintAttachments.length === props.complaint.attachments.length)
          setIsSettingAttachments(false);
    }
  }, [complaintAttachments]);

  // Adds event handlers on load and on loadstart. 
  const onFileAdd = files => {
    if(!props.canAddAttachments)
      return;
    
    files.map(({ file }) => {
      if(props.onAttachmentBeginLoad)
        props.onAttachmentBeginLoad();
      
      // Pass the raw File object directly
      props.onAttachmentAdd(file, file.name);
    });
  };

  const onFileDelete = (files: File[]) => {
    if(props.onAttachmentDelete)
      props.onAttachmentDelete(files);
  };

  if(isSettingAttachments)
    return <MSpinner />;

  return (
    <div className="file-manager">
      <MDropzone
        existingFiles={complaintAttachments}
        multiple
        onFileAdd={onFileAdd}
        onFileRemove={onFileDelete}
        prompt={props.canAddAttachments 
          ? <Typography>Click here to add files. Please note that files can not be added to feedback after it is submitted</Typography> 
          : <Typography>This feedback has no attachments</Typography>}
        fileRightOverrides={dropzoneRightsOverrides}
        trackFiles={true}
        columnDefinitionOverride={[
          { name: "Name", dataSource: "name", className: "file-name-cell" },
          { name: "Type", dataSource: "extension", style: { width: "50px" } },
          { name: "Actions", dataSource: "actions", style: { width: "160px" } },
        ]}
      />
    </div>
    );
};

export default EmployeeComplaintFileManager;